/**
 * AI estimate (flow from JF 2026-09-29):
 *   files + notes → step 1: the AI extracts the checklist → the AM reviews it (every item can be clicked and corrected
 *   right away) and answers the AI's questions, the answers go straight into the checklist → the AM clicks
 *   "Generate the cost sheet" → step 2: the AI prices against our history, the engine computes → cost sheet.
 * Nothing is priced before the AM confirms. Each real run is tracked for the admin page (files named, not stored).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { answerQuestions, blankSheet, cmPartsOf, setCmParts, checklistRows, CK_FACTORS, newRow, optionLabel, totals, type Answer, type Item, type Items, type Lang, type Question, type Sheet, parseQtys, styleGroups, type StyleGroup } from "@ic/engine";
import { Checklist } from "./Checklist.tsx";
import { useStore } from "../../store.ts";
import { useT } from "../../shared/i18n.ts";
import { api, type ExtractResult, type Job, type Part } from "../../shared/api.ts";
import { KEYS, lsGet, lsSet } from "../../shared/storage.ts";
import { fileToParts, type PageSel } from "./files.ts";
import { batchMinutes, busyBatch, runBatch, stopBatch, type BatchStyle } from "./batch.ts";
import { flagStale, STALE_MSG } from "../../shared/stale.ts";
import { moneyFor } from "../../shared/money.ts";
import { useGroup } from "../group/groupStore.ts";
import { okToReplace } from "../../shared/unsaved.ts";

/** What one AI request can carry (the AI takes about 32 MB; large PDFs are already shrunk to page images in files.ts) */
const MAX_UPLOAD_MB = 30;
import { myCoef, withMyDefaults } from "../cost-sheet/coef.ts";
import { withAiBase } from "../cost-sheet/aiBase.ts";

type StepState = "" | "run" | "done" | "err";
interface Step { t: string; st: StepState; x?: string }
type Phase = "idle" | "reading" | "pick" | "review" | "pricing" | "done";
interface Run {
  /** tracked run id (admin page); null for the demo or when tracking failed */
  id: string | null;
  /** Server sheet created for this estimate; re-pricing updates the same record. */
  sheetId?: string;
  /** the server job doing this estimate (JF 2026-10-16: closing the page does not stop it); none for the demo */
  jobId?: string;
  demo: boolean; style: Record<string, string>; questions: Question[];
  /** the checklist as extracted, plus the AM's own corrections */
  base: Items;
  /** items the AM corrected by hand — their answer to a question (if any) no longer applies */
  edited: string[];
}

/** The checklist with the answers given so far. `all` = also treat unanswered questions as "don't know" (for pricing). */
function withAnswers(r: Run, picks: (string | null)[], free: string[], lang: Lang, all: boolean): { items: Items; answers: Record<string, Answer> } {
  const items = structuredClone(r.base);
  const qs: Question[] = [], p: (string | null)[] = [], f: string[] = [];
  r.questions.forEach((q, i) => {
    if (r.edited.includes(q.key)) return;
    if (!all && !picks[i] && !String(free[i] || "").trim()) return; // still "to answer"
    qs.push(q); p.push(picks[i]); f.push(free[i] || "");
  });
  return { items, answers: answerQuestions(items, qs, p, f, lang) };
}

/**
 * Files on the clipboard: copied files (Explorer → Ctrl+C) or a screenshot. A screenshot comes without a useful name
 * ("image.png"), so it is named after the time it was pasted.
 */
export function pastedFiles(dt: DataTransfer | null): File[] {
  if (!dt) return [];
  const out: File[] = [];
  for (const it of Array.from(dt.items || [])) {
    if (it.kind !== "file") continue;
    const f = it.getAsFile();
    if (!f) continue;
    if (/^image\//.test(f.type) && (!f.name || /^image\.\w+$/i.test(f.name))) {
      const ext = (f.type.split("/")[1] || "png").replace("jpeg", "jpg");
      const ts = new Date().toTimeString().slice(0, 8).replace(/:/g, "");
      out.push(new File([f], `pasted_${ts}_${out.length + 1}.${ext}`, { type: f.type }));
    } else out.push(f);
  }
  return out;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));
const newId = () => (crypto.randomUUID ? crypto.randomUUID() : String(Date.now()) + Math.random().toString(36).slice(2));
const track = (fn: () => Promise<unknown>) => { fn().catch((e) => console.warn("[tracking]", e)); };

export function AiTab() {
  const t = useT();
  const lang = useStore((s) => s.lang);
  const meta = useStore((s) => s.meta);
  const { go, setSheet, toast } = useStore.getState();
  const [files, setFiles] = useState<File[]>([]);
  const [notes, setNotes] = useState("");
  const [ask, setAsk] = useState<boolean>(!!lsGet<number>(KEYS.ask, 1));
  // tiered quote (JF 2026-10-12): optional, off by default — off = one quantity as before
  const [tierOn, setTierOn] = useState(false);
  const [tierQ, setTierQ] = useState("");
  const [over, setOver] = useState(false);
  const [phase, setPhase] = useState<Phase>("idle");
  const [steps, setSteps] = useState<Step[]>([]);
  const [run, setRun] = useState<Run | null>(null);
  const [picks, setPicks] = useState<(string | null)[]>([]);
  const [free, setFree] = useState<string[]>([]);
  const [dirty, setDirty] = useState(false); // checklist changed after the cost sheet was made
  // several different styles in one upload → the AM picks one before anything is extracted (JF 2026-10-12)
  const [multi, setMulti] = useState<{ groups: StyleGroup[]; loose: number[]; files: File[] } | null>(null);
  const input = useRef<HTMLInputElement>(null);
  // the server job running for this page now ("stop" stops it)
  const jobRef = useRef<string | null>(null);
  const [jobLive, setJobLive] = useState(false);
  // "my estimates" (top bar) asks to continue one here
  const resumeId = useStore((s) => s.resumeJob);
  // every style of a multi-style upload estimated one after the other (JF 2026-10-13)
  const [batch, setBatch] = useState<BatchStyle[] | null>(null);
  const [batchGroup, setBatchGroup] = useState<string | null>(null);
  const [batchRun, setBatchRun] = useState(false);
  const [batchT0, setBatchT0] = useState(0);
  const [batchMin, setBatchMin] = useState(0); // the estimate shown before starting, in minutes
  const [, tick] = useState(0);
  // while a batch runs: the clock in the overall progress moves every second
  useEffect(() => { if (!batchRun) return; const h = window.setInterval(() => tick((x) => x + 1), 1000); return () => window.clearInterval(h); }, [batchRun]);
  const cur = useStore((s) => s.cur);
  const dfx = useStore((s) => s.defaultFx);
  const busy = phase === "reading" || phase === "pricing" || batchRun;

  // the AI key lives on the server (ANTHROPIC_API_KEY); without it only the demo works
  const aiReady = !!meta?.ai.configured;

  const shown = useMemo(() => (run ? withAnswers(run, picks, free, lang, false).items : null), [run, picks, free, lang]);
  const open = run && run.questions.length ? run.questions.filter((q, i) => !run.edited.includes(q.key) && !picks[i] && !String(free[i] || "").trim()).length : 0;

  const stepSet = (i: number, st: StepState, x?: string) => setSteps((s) => s.map((v, j) => (j === i ? { ...v, st, x: x ?? v.x } : v)));
  const fail = (e: unknown, r?: Run | null) => {
    const msg = flagStale(e) ? STALE_MSG : e instanceof Error ? e.message : String(e);
    setSteps((s) => { const i = Math.max(0, s.findIndex((v) => v.st === "run")); return s.map((v, j) => (j === i ? { ...v, st: "err", x: msg } : v)); });
    const id = r?.id;
    if (id) track(() => api.runPatch(id, { status: "failed", error: msg.slice(0, 2000) }));
  };
  // copy the list now: the input is reset right after, which empties its live FileList before React applies the update
  const addFiles = (list: FileList | File[] | null) => { if (!list) return; const add = Array.from(list); setFiles((f) => [...f, ...add]); };

  // Ctrl+V on the AI tab adds pasted screenshots / copied files (JF 2026-10-12); pasted text still goes into the notes box
  const view = useStore((s) => s.view);
  const pasteOk = view === "ai" && aiReady && !busy;
  useEffect(() => {
    if (!pasteOk) return;
    const onPaste = (e: ClipboardEvent) => {
      // in a text box, text wins (Excel cells come with a picture of the cells as well)
      const el = e.target as HTMLElement | null;
      if (el && /^(TEXTAREA|INPUT)$/.test(el.tagName) && e.clipboardData?.types.includes("text/plain")) return;
      const got = pastedFiles(e.clipboardData);
      if (!got.length) return;
      e.preventDefault();
      addFiles(got);
      toast(t(`Pasted ${got.length} file(s)`, `已粘贴 ${got.length} 个文件`));
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
  }, [pasteOk, lang]); // eslint-disable-line react-hooks/exhaustive-deps

  /** the pages of file n for style g when one file holds several styles (else the whole file) — JF 2026-10-13 */
  function pageSel(g: StyleGroup, n: number): PageSel | undefined {
    const own = g.pages?.[String(n)];
    if (!multi || !own?.length) return undefined;
    return { own, others: multi.groups.filter((x) => x !== g).flatMap((x) => x.pages?.[String(n)] || []) };
  }
  /** Several styles: keep the picked style's files (+ files with no style, e.g. a price list) and read them again. */
  function pickStyle(g: StyleGroup) {
    if (!multi) return;
    const nums = [...g.files, ...multi.loose].sort((x, y) => x - y);
    const keep = nums.map((n) => multi.files[n - 1]).filter(Boolean);
    const sel = new Map<File, PageSel>();
    for (const n of nums) { const p = pageSel(g, n); if (p) sel.set(multi.files[n - 1], p); }
    // the file list keeps the whole files; only this style's pages are read
    if (!sel.size) setFiles(keep);
    start(false, keep, sel.size ? { sel, label: [g.style_no, g.style_name].filter(Boolean).join(" · ") } : undefined);
  }
  function repick() { setMulti(null); setSteps([]); setPhase("idle"); document.getElementById("drop")?.scrollIntoView({ block: "center" }); }

  /** all the styles: each read and priced on its own ("don't ask"), saved, then grouped (JF 2026-10-13) */
  async function costAll() {
    if (!multi || batchRun) return;
    const nS = multi.groups.length;
    const tiers = tierOn ? parseQtys(tierQ) : null;
    if (!confirm(t(`Estimate all ${nS} styles one after the other? About ${batchMinutes(nS, !!tierOn)} minutes, ${nS * 2} AI calls. Missing information is assumed (each sheet can be corrected afterwards). Each style is saved and they are put into one style group.`,
      `逐款估价全部 ${nS} 款？大约 ${batchMinutes(nS, !!tierOn)} 分钟，调用 AI ${nS * 2} 次。缺的信息由 AI 假设（之后每张都可以修改）。每款各存一张成本表，最后放进同一个款式组合。`))) return;
    const list: BatchStyle[] = multi.groups.map((g) => ({
      key: g.key, style_no: g.style_no, style_name: g.style_name, status: "wait",
      files: [...g.files, ...multi.loose].sort((a, b) => a - b).map((k) => multi.files[k - 1]).filter(Boolean),
      pages: [...g.files, ...multi.loose].sort((a, b) => a - b).filter((k) => multi.files[k - 1]).map((k) => pageSel(g, k)),
    }));
    setBatch(list); setBatchGroup(null); setBatchRun(true); setBatchT0(Date.now()); setBatchMin(batchMinutes(nS, !!tierOn));
    stepSet(2, "run", t("estimating every style, one after the other", "正在逐款估价"));
    try {
      const r = await runBatch(list, { notes, lang, tiers, onUpdate: setBatch, t });
      setBatchGroup(r.groupId);
      stepSet(2, r.done ? "done" : "err", t(`${r.done} of ${nS} styles estimated and saved`, `${nS} 款里完成 ${r.done} 款，已保存`));
      if (r.groupId) toast(t("All done — the styles are in one style group", "全部完成——这些款已放进一个款式组合"));
    } catch (e) { toast(e instanceof Error ? e.message : String(e)); } finally { setBatchRun(false); }
  }
  async function openBatchGroup() {
    if (!batchGroup) return;
    await useGroup.getState().open(batchGroup);
    go("group");
  }

  /**
   * While an AI call runs: under its step, how far the AI has written, the time so far and how long it usually takes
   * (JF 2026-10-13 — a tiered quote took minutes with no sign of life). Returns the function that stops the polling.
   */
  function watch(step: number, kind: "extract" | "price" | "price_tiers", id: string): () => void {
    const t0 = Date.now();
    let typ = 0, stopped = false;
    const show = (x: { elapsedMs?: number; typicalMs?: number; lines?: number; tierQty?: number; items?: number } = {}) => {
      if (stopped) return;
      typ = x.typicalMs || typ;
      const sec = Math.round((x.elapsedMs ?? Date.now() - t0) / 1000);
      const what = kind === "extract"
        ? (x.items ? t(`the AI is writing checklist item ${Math.min(x.items, CK_FACTORS)} / ${CK_FACTORS}`, `AI 正在写第 ${Math.min(x.items, CK_FACTORS)} / ${CK_FACTORS} 项清单`) : t("the AI is reading the files", "AI 正在读文件"))
        : x.tierQty ? t(`the AI is writing the tiered quote, quantity ${x.tierQty}`, `AI 正在写阶梯报价第 ${x.tierQty} 档`)
        : x.lines ? t(`the AI is writing line ${x.lines} (fabric / trim)`, `AI 正在写第 ${x.lines} 行（面料 / 辅料）`)
        : t("the AI is weighing the checklist against our history", "AI 正在对照公司历史判断价格");
      const usual = typ ? t(` · usually about ${Math.round(typ / 1000)} s`, ` · 通常约 ${Math.round(typ / 1000)} 秒`) : "";
      const slow = typ && sec * 1000 > typ * 1.5 ? t(" · slower than usual, please wait", " · 比平时慢，请再等等") : "";
      stepSet(step, "run", `${what} · ${t(`${sec} s so far`, `已用 ${sec} 秒`)}${usual}${slow}`);
    };
    const tick = () => { api.aiProgress(id, kind).then(show).catch(() => show()); };
    tick();
    const h = window.setInterval(tick, 1500);
    return () => {
      stopped = true; window.clearInterval(h);
      stepSet(step, "run", t(`took ${Math.round((Date.now() - t0) / 1000)} s`, `用时 ${Math.round((Date.now() - t0) / 1000)} 秒`)); // replaced by the result / error right after
    };
  }

  /**
   * Follow a server job (JF 2026-10-16) until it waits for the AM or ends: the AI's progress under the step it is on.
   * Leaving the page changes nothing on the server; "my estimates" (top bar) picks it up again.
   */
  async function follow(jobId: string): Promise<Job> {
    let pid: string | null = null, stop = () => {};
    const t0 = Date.now(), first = await api.job(jobId).then((x) => (x.extract ? 3 : 1)).catch(() => 1);
    jobRef.current = jobId; setJobLive(true);
    try {
      for (;;) {
        const j = await api.job(jobId);
        if (j.status === "answer" || j.status === "done" || j.status === "err" || j.status === "cancelled") {
          // how long it took, also when it ended between two looks
          stop(); stop = () => {};
          if (j.status === "answer" || j.status === "done") stepSet(j.status === "answer" ? 1 : first, "run", t(`took ${Math.round((Date.now() - t0) / 1000)} s`, `用时 ${Math.round((Date.now() - t0) / 1000)} 秒`));
          return j;
        }
        if (j.status === "queued") {
          stop(); stop = () => {}; pid = null;
          stepSet(j.extract ? 3 : 1, "run", t("queued on the server — you can close this page; \"my estimates\" at the top shows it", "服务器排队中——可以关页面，进度在顶部「我的估价」"));
        } else if (j.status === "save") {
          stop(); stop = () => {}; pid = null;
          stepSet(3, "done"); stepSet(4, "run", t("Saving the cost sheet to the server", "正在自动保存 Cost Sheet 到服务器"));
        } else if (j.pid && j.pid !== pid) {
          stop(); pid = j.pid;
          if (j.status === "price") { stepSet(0, "done"); stepSet(1, "done"); stepSet(2, "done"); }
          stop = watch(j.status === "extract" ? 1 : 3, j.pkind, j.pid);
        }
        await sleep(1500);
      }
    } finally { stop(); setJobLive(false); }
  }
  /** the server priced and saved it: open the saved sheet */
  async function openDone(j: Job, r: Run) {
    const saved = await api.sheet(j.sheetId!);
    r.sheetId = saved.id;
    setSheet(saved.data, { id: saved.id }); useStore.getState().markSaved(saved.data);
    stepSet(3, "done"); stepSet(4, "done", t("Cost sheet saved automatically", "Cost Sheet 已自动保存到服务器"));
    jobRef.current = null;
    setPhase("done");
    setTimeout(() => go("sheet"), 350);
  }
  async function stopJob() {
    const id = jobRef.current;
    if (!id || !confirm(t("Stop this estimate? The AI call is stopped.", "停止这次估价？AI 会马上停下。"))) return;
    await api.jobCancel(id).catch((e) => toast(String(e.message || e)));
  }
  /** continue an estimate from "my estimates": answer the questions, or open the finished sheet */
  async function resume(jobId: string) {
    useStore.getState().setResumeJob(null);
    if (busy || !okToReplace()) return;
    setRun(null); setDirty(false); setMulti(null); setBatch(null); setBatchGroup(null);
    setSteps([
      t("Read the files", "读取文件"), t(`AI extracts the ${CK_FACTORS} checklist items`, `AI 提取 ${CK_FACTORS} 项清单`),
      t("You check the checklist and answer the questions", "你核对清单、回答问题"),
      t("AI prices it against our history (unit price, yield, CM) · the program computes the FOB", "AI 参照公司历史价定单价 / 用量 / CM · 程序计算 FOB"),
      t("Fill the cost sheet", "填入 cost sheet"),
    ].map((x, i) => ({ t: x, st: i === 0 ? "done" : "" })));
    try {
      let j = await api.job(jobId);
      if (!["answer", "done", "err", "cancelled"].includes(j.status)) { setPhase(j.extract ? "pricing" : "reading"); j = await follow(jobId); }
      if (j.status === "err" || j.status === "cancelled") throw new Error(j.error || t("Stopped", "已停止"));
      const ex = j.extract!;
      const r: Run = { id: j.runId, demo: false, base: ex.items, style: ex.style, questions: ex.questions, edited: [], jobId, sheetId: j.sheetId || undefined };
      setRun(r); setPicks(r.questions.map(() => null)); setFree(r.questions.map(() => ""));
      stepSet(1, "done");
      if (j.status === "done") { stepSet(2, "done"); await openDone(j, r); return; }
      if (ex.multiStyle || ex.nothingFound) {
        stepSet(1, "err", t("The AI could not tell which style to cost from these files — upload them again (only that style, or name it in the notes).", "AI 没能从这些文件里确定要估哪个款——请重新上传（只传这一款，或在补充说明里写明款号）。"));
        setPhase("idle"); return;
      }
      stepSet(2, "run", r.questions.length
        ? t(`${r.questions.length} question(s) below · click any checklist item on the right to correct it`, `下面有 ${r.questions.length} 个问题 · 右边清单任一项可点击修改`)
        : t("Check the checklist on the right — click any item to correct it", "请核对右边清单——任一项可点击修改"));
      setPhase("review");
    } catch (e) { setPhase("idle"); fail(e); }
  }
  useEffect(() => { if (resumeId) resume(resumeId); }, [resumeId]); // eslint-disable-line react-hooks/exhaustive-deps

  function editItem(key: string, patch: Item) {
    setRun((r) => (r ? { ...r, base: { ...r.base, [key]: patch }, edited: r.edited.includes(key) ? r.edited : [...r.edited, key] } : r));
    if (phase === "done") setDirty(true);
  }

  async function start(demo: boolean, only?: File[], pick?: { sel: Map<File, PageSel>; label: string }) {
    if (busy) return;
    if (!only && !okToReplace()) return; // a new estimate replaces the sheet being edited (JF 2026-10-13)
    const list = only || files; // a picked style's files (the state update is not applied yet)
    if (!demo) {
      if (!aiReady) return;
      if (!list.length && !notes.trim()) { toast(t("Add a tech pack or write some notes first.", "请先上传技术包或写几句说明。")); return; }
    }
    setPhase("reading"); setRun(null); setDirty(false); setMulti(null); setBatch(null); setBatchGroup(null);
    setSteps([
      t("Read the files", "读取文件"),
      t(`AI extracts the ${CK_FACTORS} checklist items`, `AI 提取 ${CK_FACTORS} 项清单`),
      t("You check the checklist and answer the questions", "你核对清单、回答问题"),
      t("AI prices it against our history (unit price, yield, CM) · the program computes the FOB", "AI 参照公司历史价定单价 / 用量 / CM · 程序计算 FOB"),
      t("Fill the cost sheet", "填入 cost sheet"),
    ].map((x, i) => ({ t: x, st: i === 0 ? "run" : "" })));
    let r: Run | null = null;
    let id: string | null = null;
    try {
      // (a) tracked: one "upload files → start parsing" — the server's job records it (not for the demo)
      const parts: Part[] = [];
      if (!demo) {
        for (const [i, f] of list.entries()) parts.push(...(await fileToParts(f, i + 1, (p, n) =>
          stepSet(0, "run", t(`PDF "${f.name}" → page images: page ${p} / ${n}`, `PDF「${f.name}」转成图片：第 ${p} / ${n} 页`)), pick?.sel.get(f))));
        if (pick) parts.push({ type: "text", text: `[STYLE TO COST] Cost ONLY this style: ${pick.label}. Ignore other styles that appear in shared pages or files.` });
        if (notes.trim()) parts.push({ type: "text", text: "[AM NOTES]\n" + notes.trim() });
        // the server takes 60 MB per request and the AI about 32 MB — say so instead of a bare "HTTP 413" (JF 2026-10-12)
        const mb = new Blob([JSON.stringify(parts)]).size / 1024 / 1024;
        if (mb > MAX_UPLOAD_MB) throw new Error(t(
          `The files are too large for the AI (${mb.toFixed(1)} MB after conversion, limit ${MAX_UPLOAD_MB} MB). Upload fewer files or only the pages you need.`,
          `文件太大，AI 一次读不了（转换后 ${mb.toFixed(1)} MB，上限 ${MAX_UPLOAD_MB} MB）。请少传几个文件，或只传需要的页。`));
      }
      stepSet(0, "done", demo ? t("Demo: men's cotton poplin shirt", "演示：男装全棉府绸衬衫") : list.length + " " + t("file(s)", "个文件"));
      stepSet(1, "run");
      let ex: ExtractResult;
      let jobId: string | undefined;
      if (demo) ex = await api.extract({ parts, ask, lang, demo });
      else {
        // the server reads the files (JF 2026-10-16): closing the page does not stop it — "my estimates" at the top shows it
        const j = await api.jobCreate({ kind: "single", title: pick?.label || list.map((f) => f.name).join(", ").slice(0, 280) || t("notes only", "仅补充说明"), parts, ask, lang,
          tiers: tierOn ? parseQtys(tierQ) : null, notes: notes.trim(), files: list.map((f) => ({ name: f.name, size: f.size, type: f.type })), nFiles: list.length, picked: !!pick });
        jobId = j.id; id = j.runId;
        const fin = await follow(j.id);
        if (fin.status === "err" || fin.status === "cancelled") throw new Error(fin.error || t("Stopped", "已停止"));
        ex = fin.extract!;
        if (fin.status === "done") {
          // "don't ask" and nothing to check: the server already priced and saved it
          r = { id, demo, base: ex.items, style: ex.style, questions: ex.questions, edited: [], jobId, sheetId: fin.sheetId || undefined };
          setRun(r); setPicks([]); setFree([]);
          stepSet(1, "done"); stepSet(2, "done", t("not asked — the AI assumed what was missing (correct it on the right and re-price if needed)", "不问——缺的信息由 AI 假设（需要的话可在右边清单修改后重新定价）"));
          await openDone(fin, r);
          return;
        }
      }
      const sg = demo || pick ? null : styleGroups(ex.files, list.length, ex.styles);
      if (sg && sg.groups.length > 1) {
        stepSet(1, "done", t(`${sg.groups.length} different styles found in the files`, `文件里有 ${sg.groups.length} 个不同的款`));
        stepSet(2, "run", t("Pick the style to cost below", "请在下面选要估的款"));
        setMulti({ ...sg, files: list });
        setPhase("pick");
        if (id) track(() => api.runPatch(id!, { status: "failed", error: "several styles uploaded — the AM picks one / 上传了多个款，请用户选择" }));
        return;
      }
      // what the AI read in each file — shown in both modes, so "AI assumed" never hides an unread file (JF 2026-10-12)
      const read = (ex.files || []).filter((f) => f.summary).map((f) => `${list[f.file - 1]?.name || t("file", "文件") + " " + f.file}: ${f.summary}`).join("  ｜  ");
      if (read) stepSet(0, "done", read);
      const stop = (en: string, zh: string) => {
        const msg = t(en, zh);
        stepSet(1, "err", msg); setPhase("idle");
        if (id) track(() => api.runPatch(id!, { status: "failed", error: msg }));
      };
      const empty = Object.values(ex.items || {}).every((it) => !String((it as { value?: unknown })?.value ?? "").trim());
      if (!demo && (ex.nothingFound || empty)) return stop(
        "The AI could not read anything to cost from these files (see what it saw above). Upload the tech pack / spec, or describe the style in the notes.",
        "AI 没从这些文件里读到可以估价的款式信息（它看到的内容见上面）。请上传 tech pack / 规格表，或在补充说明里写清楚款式。");
      if (!demo && ex.multiStyle && !pick) return stop(
        "These files show several styles and the AI cannot tell which one to cost. Upload only that style, or name it (style no. / name) in the notes.",
        "文件里有多个款，AI 分不清要估哪一个。请只上传这一款的资料，或在补充说明里写明款号 / 款名。");
      r = { id, demo, base: ex.items, style: ex.style, questions: ex.questions, edited: [], jobId };
      if (id) track(() => api.runPatch(id!, { status: "extracted", style: ex.style, items: ex.items, questions: ex.questions }));
      setRun(r);
      // quantities the files ask for → fill the tiered-quote box if it is on and empty
      const qt = parseQtys((ex.items.qty || {}).tiers);
      if (tierOn && !tierQ.trim() && qt.length) setTierQ(qt.join(" / "));
      setPicks(r.questions.map(() => null)); setFree(r.questions.map(() => ""));
      stepSet(1, "done");
      stepSet(2, "run", r.questions.length
        ? t(`${r.questions.length} question(s) below · click any checklist item on the right to correct it`, `下面有 ${r.questions.length} 个问题 · 右边清单任一项可点击修改`)
        : t("Check the checklist on the right — click any item to correct it", "请核对右边清单——任一项可点击修改"));
      setPhase("review");
      // "don't ask — assume": straight to the cost sheet, no stop at the checklist (JF 2026-10-12); it can still be corrected and re-priced afterwards
      if (!ask && !r.questions.length) await price(r, false, true);
    } catch (e) { setPhase("idle"); fail(e, r ?? (id ? ({ id } as Run) : null)); }
  }

  async function price(r: Run, again: boolean, auto = false) {
    const { items, answers } = withAnswers(r, picks, free, lang, true);
    const asked = r.questions.length > 0;
    setPhase("pricing"); setDirty(false);
    stepSet(2, "done", again ? t("checklist changed", "清单已修改") : auto ? t("not asked — the AI assumed what was missing (correct it on the right and re-price if needed)", "不问——缺的信息由 AI 假设（需要的话可在右边清单修改后重新定价）") : asked ? Object.values(answers).filter(Boolean).length + " " + t("answered", "题已回答") : t("checked", "已核对"));
    stepSet(3, "run"); stepSet(4, "");
    try {
      const keepQuote = true; // Quote mode is on by default for every new estimate (JF 2026-10-13)
      // quantities typed at the start, else the ones the AI read from the files
      const tiers = tierOn ? (parseQtys(tierQ).length ? parseQtys(tierQ) : parseQtys((items.qty || {}).tiers)) : [];
      if (!r.demo && r.jobId) {
        // priced and saved by the server (JF 2026-10-16): the page may be closed meanwhile
        await api.jobPrice(r.jobId, { items, answers, asked, tiers });
        const fin = await follow(r.jobId);
        if (fin.status !== "done") throw new Error(fin.error || t("Stopped", "已停止"));
        await openDone(fin, r);
        return;
      }
      const pid = r.demo ? undefined : newId();
      const stopWatch = pid ? watch(3, tiers.length ? "price_tiers" : "price", pid) : () => {};
      const res = await api.price({ style: r.style, items, answers, asked, lang, demo: r.demo, keepQuote, coef: myCoef(), tiers, progressId: pid, fx: useStore.getState().defaultFx }).finally(stopWatch);
      stepSet(3, "done"); stepSet(4, "run");
      const sheet: Sheet = res.sheet;
      if (r.id && sheet.ai) sheet.ai.run = r.id;
      // my default coefficients for its quantity / each quantity of a tiered quote (JF 2026-10-13)
      withMyDefaults(sheet);
      withAiBase(sheet); // what "reset to the AI result" goes back to
      setSheet(sheet, { id: null });
      // the demo is not saved: every click would leave another sample sheet in "my sheets"
      if (r.demo) stepSet(4, "done");
      else try {
        stepSet(4, "run", t("Saving the cost sheet to the server", "正在自动保存 Cost Sheet 到服务器"));
        const saved = r.sheetId
          ? await api.updateSheet(r.sheetId, sheet)
          : await api.createSheet(sheet);
        r.sheetId = saved.id;
        setSheet(sheet, { id: saved.id });
        useStore.getState().markSaved(sheet);
        stepSet(4, "done", t("Cost sheet saved automatically", "Cost Sheet 已自动保存到服务器"));
      } catch (error) {
        // Keep the completed estimate available locally so the user can retry saving.
        setSheet(sheet, { id: r.sheetId ?? null });
        const detail = error instanceof Error ? error.message : String(error);
        const message = t("Estimate completed, but server save failed. Please save the cost sheet again: ", "估价已完成，但服务器保存失败，请在 Cost Sheet 页面重新保存：") + detail;
        stepSet(4, "err", message);
        toast(message);
      }
      if (r.id) {
        const tt = totals(sheet);
        track(() => api.runPatch(r.id!, {
          status: "priced", answers, finalItems: items, price: res.price,
          totals: { fob: tt.fob, qfob: tt.qfob, grand: tt.grand, fab: tt.fab, trim: tt.trim, cm: tt.cm, qcm: tt.qcm, gp: tt.gp, gpp: tt.gpp },
        }));
      }
      setPhase("done");
      setTimeout(() => go("sheet"), 350);
    } catch (e) { setPhase("review"); fail(e, r); }
  }

  function blank() {
    if (!okToReplace()) { go("sheet"); return; }
    const s = blankSheet(undefined, myCoef()); s.from = "ai"; s.quote = true; s.fab.push(newRow("fab")); s.trim.push(newRow("trim"));
    // the company rate (customs & document fees: no fixed ¥3,000 any more, JF 2026-10-16)
    s.fx = useStore.getState().defaultFx;
    const p = cmPartsOf(s); p.cmt = { px: "", desc: "" }; setCmParts(s, p);
    setSheet(s, { id: null }); useStore.getState().markSaved(s); go("sheet");
  }
  const pick = (qi: number, v: string | null) => setPicks((p) => p.map((x, j) => (j === qi ? (x === v ? null : v) : x)));

  return (
    <div className="aigrid">
      <div className="panel">
        {aiReady ? (
          <>
            <div className={"drop" + (over ? " over" : "")} id="drop" onClick={() => input.current?.click()}
              onDragEnter={(e) => { e.preventDefault(); setOver(true); }} onDragOver={(e) => { e.preventDefault(); setOver(true); }}
              onDragLeave={(e) => { e.preventDefault(); setOver(false); }} onDrop={(e) => { e.preventDefault(); setOver(false); addFiles(e.dataTransfer.files); }}>
              <b>{t("Drop the tech pack here, or click to choose", "把技术包拖到这里，或点击选择")}</b>
              <span>{t("PDF, images, Excel, Outlook .msg — several files are fine · or Ctrl+V to paste a screenshot / copied files", "支持 PDF、图片、Excel、Outlook 邮件 .msg，可多选 · 也可以 Ctrl+V 粘贴截图或复制的文件")}</span>
              <span className="dim" style={{ fontSize: 12 }} id="multiHint">{t("Several styles at once (several tech packs, or one brief / line sheet with several styles) are fine — the AI finds them and can estimate them all, one by one.",
                "可以一次传多款（多份 tech pack，或一份里有多款的设计手册 / line sheet）——AI 会认出每一款，可以全部逐款估价。")}</span>
            </div>
            <input type="file" id="aiFiles" ref={input} multiple style={{ display: "none" }} accept=".pdf,.png,.jpg,.jpeg,.webp,.gif,.xlsx,.xls,.csv,.msg,image/*"
              onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
            <div className="files" id="fileList">{files.map((f, i) => (
              <span className="fchip" key={i}>{f.name} <span className="dim">{Math.max(1, Math.round(f.size / 1024))} KB</span>
                <button onClick={() => setFiles((x) => x.filter((_, j) => j !== i))}>×</button></span>
            ))}</div>
            <textarea className="in" id="aiNotes" value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder={t("Anything the files don't say — qty, target price, fabric source, ship date… (optional)", "文件里没写的补充——数量、目标价、面料来源、交期……（可选）")} />
          </>
        ) : (
          <div className="warn" id="needKey" style={{ marginBottom: 12 }}>
            <b style={{ color: "var(--ink)" }}>{t("AI is not available yet.", "AI 还不能用。")}</b>
            <div style={{ marginTop: 4 }}>{t("The server has no AI key (ANTHROPIC_API_KEY) — ask the administrator. The demo below still works.",
              "服务器还没配置 AI key（ANTHROPIC_API_KEY）——请联系管理员。下面的演示照常可用。")}</div>
          </div>
        )}
        <div className="askrow">
          <div className="seg" id="askSeg">
            <button data-ask="1" className={ask ? "on" : ""} onClick={() => { setAsk(true); lsSet(KEYS.ask, 1); }}>{t("AI may ask me", "可以问我")}</button>
            <button data-ask="0" className={!ask ? "on" : ""} onClick={() => { setAsk(false); lsSet(KEYS.ask, 0); }}>{t("Don't ask — assume", "不问，直接假设")}</button>
          </div>
          <div className="askhint">{ask ? t("The AI asks at most 6 questions, only for what really moves the cost.", "AI 最多问 6 个问题，只问真正影响成本的。")
            : t("No questions — anything missing is filled with a sensible default, marked “AI assumed”, and the cost sheet comes straight away.", "不提问——缺的信息用合理默认值补上、标成「AI 假设」，直接出成本表。")}</div>
        </div>
        <div className="askrow" id="tierRow">
          <span className={"switch" + (tierOn ? " on" : "")} id="tierSwitch" onClick={() => setTierOn(!tierOn)}><i></i><span>{t("Tiered quote", "阶梯报价")}</span></span>
          {tierOn && <input className="in" id="tierQtys" style={{ maxWidth: 220 }} value={tierQ} onChange={(e) => setTierQ(e.target.value)}
            placeholder={t("e.g. 100 / 300 / 1000", "例：100 / 300 / 1000")} />}
          <div className="askhint">{tierOn
            ? t("The AI prices every quantity itself (unit price, yield, CM per line). Empty = the quantities the files ask for.", "AI 按每个数量分别定价（每行单价、用量、CM）。不填 = 用文件里写的数量。")
            : t("Optional — turn on when the customer wants prices for several quantities.", "可选——客户要多个数量的报价时打开。")}</div>
        </div>
        <div className="gorow">
          {aiReady && <button className="btn p" id="aiGo" disabled={busy} onClick={() => start(false)}>{t("Start estimate", "开始估价")}</button>}
          <button className="btn" id="aiDemo" disabled={busy} onClick={() => start(true)}>{t("Try demo (no key needed)", "演示一遍（不用密钥）")}</button>
          <span className="dim" style={{ fontSize: 12.5 }}>{t("or", "或")}</span>
          <span className="lnk" id="blankSheet" onClick={blank}>{t("open a blank cost sheet", "打开空白 cost sheet 手动填")}</span>
        </div>
        <div className={"steps" + (steps.length ? " on" : "")} id="steps">{steps.map((s, i) => (
          <div className={"stp " + s.st} key={i}><i>{s.st === "done" ? "✓" : s.st === "err" ? "!" : ""}</i>
            <div>{s.t}{s.x && <div className="dim" style={{ fontSize: 12 }}>{s.x}</div>}</div></div>
        ))}
          {jobLive && <div className="dim" id="jobNote" style={{ fontSize: 12, display: "flex", gap: 10, alignItems: "center", marginTop: 4 }}>
            {t("Running on the server — closing this page does not stop it (\"my estimates\" at the top).", "在服务器上运行——关页面不会停（顶部「我的估价」可以看）。")}
            <button className="btn sm" id="jobStop" onClick={stopJob}>{t("Stop", "停止")}</button></div>}
        </div>
        {phase === "pick" && multi && (
          <div className="qwrap on" id="stylePick">
            <div style={{ fontWeight: 700, marginBottom: 4 }}>{t(`These files are ${multi.groups.length} different styles — which one should be costed?`, `这些文件是 ${multi.groups.length} 个不同的款——这次要估哪个？`)}</div>
            <div className="dim" style={{ fontSize: 12.5, marginBottom: 10 }}>{t("One cost sheet is one style. Pick one (only its files are read again), or go back and change the files.",
              "一张成本表只估一个款。选一个款（只重新读它的文件），或者返回改上传的文件。")}</div>
            {multi.groups.map((g, gi) => (
              <div className="qcard" key={g.key} data-style={gi}>
                <div className="qk">{t("Style", "款")} {gi + 1}{g.category ? " · " + g.category : ""}</div>
                <div className="qt">{[g.style_no, g.style_name].filter(Boolean).join(" · ")}</div>
                <div className="dim" style={{ fontSize: 12.5, margin: "4px 0 8px" }}>{g.files.map((n) => (multi.files[n - 1]?.name || "") + (g.pages?.[String(n)]?.length ? t(` (page ${g.pages[String(n)].join(", ")})`, `（第 ${g.pages[String(n)].join("、")} 页）`) : "")).join(" ／ ")}
                  {multi.loose.length > 0 && <> {t("+ shared:", "+ 共用：")} {multi.loose.map((n) => multi.files[n - 1]?.name).join(" ／ ")}</>}</div>
                <button className="btn p sm" data-pick-style={gi} disabled={batchRun} onClick={() => pickStyle(g)}>{t("Cost only this style", "只估这个款")}</button>
              </div>
            ))}
            <div className="gorow">
              <button className="btn p" id="costAll" disabled={batchRun} onClick={costAll}
                data-tipx={t("Each style read and priced on its own, without questions; saved and put together in one style group", "每款单独读取、定价（不提问）；各自保存，并放进同一个款式组合")}>
                {t(`Estimate all ${multi.groups.length} styles`, `全部逐款估价（${multi.groups.length} 款）`)}</button>
              <button className="btn" id="repick" onClick={repick}>{t("Wrong files — let me change them", "传错了，重新选文件")}</button></div>
          </div>
        )}
        {batch && (
          <div className="qwrap on" id="batchBox">
            <div style={{ fontWeight: 700, marginBottom: 4, display: "flex", alignItems: "center", gap: 10 }}>{t("Every style, one after the other", "逐款估价")}
              {batchRun && <button className="btn sm" id="batchStop" style={{ marginLeft: "auto" }} onClick={() => { if (confirm(t("Stop the styles not done yet? Those already estimated stay saved.", "停止还没估完的款？已经估完的会保留。"))) stopBatch(); }}>{t("Stop", "停止")}</button>}</div>
            {/* the server does the work (JF 2026-10-16): once every style is handed over the page may be closed */}
            {batchRun && <div className="dim" id="batchServer" style={{ fontSize: 12, marginBottom: 6 }}>{busyBatch()
              ? t("Handing the files to the server — please keep this page open until every style says “on the server”.", "正在把文件交给服务器——请等每一款都显示「已交给服务器」再关页面。")
              : t("Everything is on the server: you can close this page — the estimates go on, see “my estimates” at the top.", "已全部交给服务器：现在可以关页面，估价会继续，进度在顶部「我的估价」里看。")}</div>}
            {(() => {
              // overall: styles done, time so far, time left (from the styles finished so far, else the estimate shown at the start)
              const fin = batch.filter((b) => b.status === "done" || b.status === "err");
              const took = fin.filter((b) => b.t0 && b.t1).map((b) => b.t1! - b.t0!);
              const per = took.length ? took.reduce((a, x) => a + x, 0) / took.length : (batchMin * 60000) / Math.max(1, batch.length);
              const left = Math.max(0, (batch.length - fin.length) * per - (batchRun ? Math.min(per, Date.now() - (batch.find((b) => b.status !== "wait" && !b.t1)?.t0 || Date.now())) : 0));
              const mins = (ms: number) => (ms < 60000 ? t(`${Math.round(ms / 1000)} s`, `${Math.round(ms / 1000)} 秒`) : t(`${Math.round(ms / 60000)} min`, `${Math.round(ms / 60000)} 分钟`));
              return (
                <div className="dim" id="batchAll" style={{ fontSize: 12.5, marginBottom: 8 }}>
                  {batchRun && <i className="spin" />}{t(`${fin.length} / ${batch.length} styles done`, `已完成 ${fin.length} / ${batch.length} 款`)}
                  {batchT0 > 0 && <> · {t("time so far", "已用")} {mins((batchRun ? Date.now() : Math.max(...batch.map((b) => b.t1 || 0))) - batchT0)}</>}
                  {batchRun && <> · {t("about", "预计还要约")} {mins(left)}{lang === "en" ? " left" : ""}</>}
                  {" · "}{t("the cost sheet shows the smallest quantity first", "成本表默认显示最小数量档")}
                </div>);
            })()}
            <table className="t" id="batchTbl"><tbody>
              {batch.map((b, i) => {
                const m = moneyFor(cur, b.fx || dfx);
                const busyRow = b.status === "read" || b.status === "price" || b.status === "save";
                return (
                  <tr key={b.key} data-batch={i} className={"bt-" + b.status}>
                    <td className="mono">{b.style_no || "—"}</td><td>{b.style_name}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{busyRow && <i className="spin" />}{{ wait: t("waiting", "排队中"), read: t("reading", "读取中"), price: t("pricing", "定价中"), save: t("saving", "保存中"), done: "✓", err: "!" }[b.status]}</td>
                    <td className="dim" style={{ fontSize: 12 }} data-batch-msg={i}>{b.status === "done" && b.quotes
                      // which quantity each price is for (JF 2026-10-13), in the currency picked at the top
                      ? t("quoted FOB ", "报价 FOB ") + b.quotes.map((q) => `${q.qty ? t(`${q.qty.toLocaleString()} pcs`, `${q.qty.toLocaleString()} 件`) : ""} ${m.fmt(q.qfob)}`).join(" · ")
                      : b.msg}</td>
                  </tr>);
              })}
            </tbody></table>
            {batchGroup && <div className="gorow"><button className="btn p" id="openBatchGroup" onClick={openBatchGroup}>{t("Open the style group", "打开款式组合")}</button></div>}
          </div>
        )}
        {phase === "review" && run && (
          <div className="qwrap on" id="qwrap">
            {run.questions.length > 0 && <div style={{ fontWeight: 700, marginBottom: 8 }}>{t("A few questions — your answers go straight into the checklist", "补充几个问题——回答会直接填进右边清单")}</div>}
            {run.questions.map((q, qi) => {
              const ck = checklistRows(null, lang).find((c) => c.key === q.key);
              const overridden = run.edited.includes(q.key);
              return (
                <div className="qcard" data-qi={qi} key={qi} style={overridden ? { opacity: 0.55 } : undefined}>
                  <div className="qk">{ck ? ck.name : q.key}{overridden && <span className="dim"> · {t("you corrected this item in the checklist", "你已在清单里直接修改")}</span>}</div>
                  <div className="qt">{lang === "zh" ? q.q_zh || q.q_en : q.q_en || q.q_zh}</div>
                  <div className="opts">
                    {(q.options || []).map((o) => (
                      <span className={"opt" + (picks[qi] === o.v ? " on" : "")} data-v={o.v} key={o.v} onClick={() => pick(qi, o.v)}>{optionLabel(o, lang)}</span>
                    ))}
                    <span className={"opt dk" + (picks[qi] === "__dk" ? " on" : "")} data-v="__dk" onClick={() => pick(qi, "__dk")}>{t("Don't know — AI decides", "不知道——让 AI 定")}</span>
                  </div>
                  <input className="in" data-qfree={qi} value={free[qi] || ""} onChange={(e) => setFree((f) => f.map((x, j) => (j === qi ? e.target.value : x)))} placeholder={t("…or type your own answer", "……或自己填写")} />
                </div>
              );
            })}
            <div className="gorow"><button className="btn p" id="qSend" onClick={() => price(run, false)}>{t("Generate the cost sheet →", "生成成本表 →")}</button>
              <span className="dim" style={{ fontSize: 12.5 }}>{open > 0 ? t(`${open} question(s) unanswered — treated as “don't know”.`, `还有 ${open} 题没回答——按「不知道」处理。`)
                : t("Check the checklist first; nothing is priced before you click.", "先核对清单；点这个按钮之前不会定价。")}</span></div>
          </div>
        )}
      </div>
      <div className="panel ckl">
        <h3>{t("What the AI checks", "AI 核对清单")}</h3>
        <div className="dim">{t(`The ${CK_FACTORS} factors that move a garment's FOB. The AI reads them from the files; the engine does the maths.`, `影响 FOB 的 ${CK_FACTORS} 项因素。AI 从文件里读，数字由引擎算。`)}</div>
        {shown && !busy && <div className="dim" style={{ fontSize: 12, marginTop: 6 }}>{t("Click an item to correct it.", "点击任一项可以修改。")}</div>}
        <div id="cklist" style={{ marginTop: 8 }}><Checklist items={shown} onEdit={shown && !busy ? editItem : undefined} /></div>
        {phase === "done" && dirty && run && (
          <div className="gorow">
            <button className="btn p" id="repriceEdited" onClick={() => price(run, true)}>{t("Re-price with these changes", "按修改重新定价")}</button>
            <span className="dim" style={{ fontSize: 12 }}>{t("Only step 2 runs again — the files are not re-read.", "只重跑第 2 步，不重新读文件。")}</span>
          </div>
        )}
      </div>
    </div>
  );
}
